import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X } from "lucide-react";

/**
 * InstallPrompt — captures the browser's `beforeinstallprompt` event and
 * surfaces a small top banner inviting the user to install the PWA.
 *
 * Design (per user feedback):
 *  • Mobile-only — desktop / tablet (md+) never sees this banner. Desktop
 *    users have the install icon in the browser URL bar and don't gain
 *    much from PWA-mode anyway.
 *  • Appears ~7s after page load (not immediately — avoids being mistaken
 *    for an interstitial ad / OS install prompt).
 *  • Dismissable, but the dismiss only suppresses the banner for ~24h —
 *    the goal is to nudge regulars until they install it.
 *  • Dual-PWA aware: customer vs /config admin tracked independently.
 *  • Auto-hides forever once `appinstalled` fires for this scope.
 */
const SHOW_DELAY_MS = 7000;
const SNOOZE_HOURS = 24; // re-appears the next day if dismissed

export default function InstallPrompt() {
    const { pathname } = useLocation();
    const isAdmin = /^\/config(\/|$)/.test(pathname || "");
    const snoozeKey = isAdmin ? "jns-pwa-snooze-admin" : "jns-pwa-snooze-customer";
    const installedKey = isAdmin ? "jns-pwa-installed-admin" : "jns-pwa-installed-customer";

    const [visible, setVisible] = useState(false);
    const promptRef = useRef(null);

    useEffect(() => {
        // Mobile-only gate. md breakpoint = 768px; we hide on anything wider.
        if (typeof window === "undefined") return;
        if (window.matchMedia("(min-width: 768px)").matches) return;
        // Already installed for this scope?
        if (localStorage.getItem(installedKey) === "1") return;
        // Already running as PWA (standalone display-mode)?
        const isStandalone = window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (isStandalone) {
            localStorage.setItem(installedKey, "1");
            return;
        }

        let timerId = null;
        const handler = (e) => {
            e.preventDefault();
            promptRef.current = e;
            const snooze = parseInt(localStorage.getItem(snoozeKey) || "0", 10);
            const hoursSince = (Date.now() - snooze) / 36e5;
            if (snooze && hoursSince < SNOOZE_HOURS) return;
            timerId = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        };
        window.addEventListener("beforeinstallprompt", handler);

        const onInstalled = () => {
            setVisible(false);
            promptRef.current = null;
            localStorage.setItem(installedKey, "1");
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            if (timerId) clearTimeout(timerId);
            window.removeEventListener("beforeinstallprompt", handler);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, [snoozeKey, installedKey]);

    const install = async () => {
        const evt = promptRef.current;
        if (!evt) return;
        evt.prompt();
        try {
            const { outcome } = await evt.userChoice;
            if (outcome === "accepted") localStorage.setItem(installedKey, "1");
        } catch { /* user dismissed native sheet */ }
        promptRef.current = null;
        setVisible(false);
    };

    const dismiss = () => {
        localStorage.setItem(snoozeKey, String(Date.now()));
        setVisible(false);
    };

    if (!visible) return null;
    const title = isAdmin ? "Install JnS Admin" : "Install JnS Creation";
    const subtitle = isAdmin ? "Inventory + orders one tap away." : "One tap from your home screen.";
    return (
        <div
            className="md:hidden fixed top-0 inset-x-0 z-[1000] bg-ink text-ivory border-b border-rosegold/40 shadow-lg shadow-stone-900/20"
            data-testid={isAdmin ? "pwa-install-banner-admin" : "pwa-install-banner"}
        >
            <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 rounded-lg bg-rosegold/20 text-rosegold flex items-center justify-center flex-shrink-0">
                    <Download size={14} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold leading-tight truncate">{title}</p>
                    <p className="text-[11px] text-ivory/70 leading-snug truncate">{subtitle}</p>
                </div>
                <button
                    type="button"
                    onClick={install}
                    data-testid="pwa-install-btn"
                    className="px-3 h-8 rounded-md bg-rosegold hover:bg-rosegold/90 text-ink text-[11px] font-bold uppercase tracking-wider flex-shrink-0"
                >
                    Install
                </button>
                <button
                    type="button"
                    onClick={dismiss}
                    data-testid="pwa-install-dismiss"
                    aria-label="Dismiss for 24 hours"
                    className="w-7 h-7 rounded-md hover:bg-ivory/10 flex items-center justify-center text-ivory/60 hover:text-ivory flex-shrink-0"
                >
                    <X size={14} />
                </button>
            </div>
        </div>
    );
}
